// "Understand this issue" (ADR-0033): what happened, why it matters, where, when, how (the data flow and the step where it goes
// wrong) and what else it touches, each fact with its evidence, and what closes it. Every fact, amount, ratio, date, row and
// the flow itself is computed by the engine from the record (issue.show → explain) and shown as published; the plain-language
// narrative is an agent's proposal a person accepts, rendered from placeholders the engine resolved (never typed digits). The UI
// decides nothing: it lays the engine's facts out, draws its flow and links each fact to where it lives.
import { Fragment, useId, useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { ActionIntent } from "../../app/ActionDrawer";
import { Button, ErrorState, IssueStatusChip, StatusPill } from "../../components";
import { fmtDate, fmtStamp } from "../../lib/format";
import {
  explainError, explainerOf, issueHref, segmentsText,
  type ExLink, type ExNarrative, type ExNode, type ExSegment, type Explainer, type Issue, type IssueRisk,
} from "../../lib/issues";
import { Who, useWs } from "./common";

const SECTIONS: Array<[string, string]> = [["what", "What happened"], ["why", "Why it matters"], ["where", "Where"], ["when", "When"],
  ["how", "How — the data flow"], ["impact", "What else it affects"], ["closes", "What closes it"]];
const anchor = (s: string) => `ex-${s.replace(/[^a-z0-9]+/gi, "-")}`;
const scrollTo = (id: string) => { const el = document.getElementById(id); el?.scrollIntoView?.({ behavior: "smooth", block: "center" }); el?.focus?.(); };

/** Where a link of the explainer leads: a route, or an anchor on this page. */
export function useExplainLink() {
  const ws = useWs();
  const navigate = useNavigate();
  const href = (l: ExLink): { to?: string; anchor?: string } => {
    switch (l.kind) {
      case "doc": return { to: `/e/${ws.id}/details/evidence?doc=${String(l.id)}` };
      case "issue": return { to: issueHref(ws.id, String(l.id)) };
      case "request": return { to: `/e/${ws.id}/documents#pbc-${String(l.id)}` };
      case "thread": return { to: `/e/${ws.id}/documents` };
      case "wp": { const w = ws.wp.find((x) => x.workpaperId === String(l.id)); return { to: `/e/${ws.id}/wp/${encodeURIComponent(w?.ref ?? String(l.id))}` }; }
      case "gl": return { anchor: anchor(`gl-${String(l.id)}`) };
      case "fs": return { anchor: anchor(`fs-${String(l.id)}`) };
      default: return { anchor: anchor(`${l.section === "gl" ? "glacct" : "acct"}-${String(l.id)}`) };
    }
  };
  const go = (l: ExLink | null) => { if (!l) return; const h = href(l); if (h.to) navigate(h.to); else if (h.anchor) scrollTo(h.anchor); };
  return { href, go };
}

function To({ link, children, className = "text-brand hover:underline" }: { link: ExLink | null; children: ReactNode; className?: string }) {
  const { href } = useExplainLink();
  if (!link) return <>{children}</>;
  const h = href(link);
  return h.to ? <Link className={className} to={h.to}>{children}</Link> : <a className={className} href={`#${h.anchor}`} onClick={(e) => { e.preventDefault(); scrollTo(h.anchor!); }}>{children}</a>;
}

const Cite = ({ c }: { c: string | null | undefined }) => (c ? <span className="font-mono text-[11px] text-muted">{c}</span> : null);

function Section({ id, title, children, action }: { id: string; title: string; children: ReactNode; action?: ReactNode }) {
  return (
    <section id={anchor(id)} aria-labelledby={`${anchor(id)}-h`} className="scroll-mt-20 border-t border-line pt-4 first:border-t-0 first:pt-0">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h3 id={`${anchor(id)}-h`} className="text-sm font-semibold">{title}</h3>{action}
      </div>
      {children}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────── the explainer

export function IssueExplainer({ issue, open }: { issue: Issue; open: (i: ActionIntent) => void }) {
  const ws = useWs();
  const err = explainError(issue);
  const x = explainerOf(issue);
  if (err) return <ErrorState error={`The engine could not explain this issue: ${err}`} />;
  if (!x) return null;
  return (
    <section aria-labelledby="understand-title" className="rounded-lg border border-brand/40 bg-raised">
      <header className="flex flex-wrap items-center justify-between gap-2 border-b border-line px-4 py-2.5">
        <div>
          <h2 id="understand-title" className="text-base font-semibold">Understand this issue</h2>
          <p className="text-xs text-muted">Every fact below is computed from the record and carries its source; the narrative is the preparer's, its figures bound to those sources.</p>
          <p className="mt-1 flex flex-wrap items-center gap-1.5 text-xs"><span className="text-muted">Status now:</span><IssueStatusChip issue={issue} /></p>
          {x.settled && <p className="mt-1 text-xs text-muted" data-settled="true">{x.settled.note}</p>}
        </div>
        <nav aria-label="Explainer sections" className="flex flex-wrap gap-1.5 text-xs">
          {SECTIONS.map(([id, label]) => <a key={id} href={`#${anchor(id)}`} onClick={(e) => { e.preventDefault(); scrollTo(anchor(id)); }}
            className="rounded-full border border-line px-2 py-0.5 hover:bg-sunken">{label.replace(" — the data flow", "")}</a>)}
        </nav>
      </header>
      <div className="space-y-5 p-4">
        <What x={x} issue={issue} open={open} engagementId={ws.id} />
        <Why x={x} issue={issue} open={open} engagementId={ws.id} />
        <Where x={x} />
        <When x={x} />
        <How x={x} />
        <Impact x={x} />
        <Closes x={x} />
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────── WHAT

/** A term of the firm's glossary in the narrative (ADR-0033 amendment 3): a dotted underline, its plain definition on hover (title), and on
 * tap or keyboard (a toggle showing it as a tooltip). The engine decides which words are terms; the "Terms used" list repeats them. */
export function GlossaryTerm({ text, term, def }: { text: string; term: string; def: string }) {
  const [open, setOpen] = useState(false);
  const id = useId();
  return (
    <span className="relative">
      <button type="button" data-term={term} title={def} aria-expanded={open} aria-describedby={open ? id : undefined}
        className="inline cursor-help p-0 text-left underline decoration-muted decoration-dotted decoration-1 underline-offset-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-focus"
        onClick={() => setOpen((o) => !o)} onBlur={() => setOpen(false)} onKeyDown={(e) => { if (e.key === "Escape") setOpen(false); }}>{text}</button>
      {open && <span role="tooltip" id={id} className="absolute left-0 top-full z-20 mt-1 block w-64 max-w-[80vw] rounded-md border border-line bg-raised px-2 py-1 text-xs font-normal text-ink shadow-pop">
        <span className="font-medium">{term}</span> — {def}</span>}
    </span>
  );
}

/** The glossary terms a narrative uses, each with its plain definition (the accessible list under the narrative). */
export function TermsUsed({ terms }: { terms: Array<{ term: string; definition: string }> | undefined }) {
  if (!terms?.length) return null;
  return (
    <details className="text-xs">
      <summary className="cursor-pointer text-muted">Terms used ({terms.length})</summary>
      <dl aria-label="Terms used" className="mt-1 space-y-1">
        {terms.map((t) => <div key={t.term}><dt className="inline font-medium">{t.term}</dt> — <dd className="inline">{t.definition}</dd></div>)}
      </dl>
    </details>
  );
}

export function Segments({ segs }: { segs: ExSegment[] }) {
  return (
    <>
      {segs.map((s, n) => s.t !== undefined ? (s.term && s.def ? <GlossaryTerm key={n} text={s.t} term={s.term} def={s.def} /> : <Fragment key={n}>{s.t}</Fragment>) : (
        <span key={n} className={`rounded px-0.5 font-medium ${s.changed ? "bg-stale-bg text-stale" : "bg-brand/10"}`} title={`${s.cite ?? s.p}${s.changed ? ` — now ${s.now ?? "does not resolve"}` : ""}`}
          data-cite={s.cite ?? undefined}>
          {s.link ? <To link={s.link} className="hover:underline">{s.text}</To> : s.text}
          {s.changed && <span className="ml-1 text-[11px]">(now {s.now ?? "—"})</span>}
        </span>
      ))}
    </>
  );
}

function NarrativeBlock({ n, tone }: { n: ExNarrative; tone: "accepted" | "proposed" }) {
  return (
    <div className={`space-y-2 rounded-md border px-3 py-2 text-sm ${tone === "proposed" ? "border-agent-line bg-agent-bg" : "border-line"}`}>
      <p aria-label={tone === "proposed" ? "Drafted: what happened" : "What happened"}><Segments segs={n.what} /></p>
      <p aria-label={tone === "proposed" ? "Drafted: why it matters" : "Why it matters, in words"}><Segments segs={n.why} /></p>
      <TermsUsed terms={n.terms} />
      <p className="text-[11px] text-muted">
        {tone === "proposed"
          ? <>The agent's draft (<span className="font-mono">{n.agent ?? n.drafted_by}</span>, for <Who name={n.drafted_by} />, {fmtStamp(n.drafted_at)}) — waiting for a person to accept it.</>
          : <>Accepted by <Who name={n.accepted_by} />{n.accepted_at ? `, ${fmtStamp(n.accepted_at)}` : ""}{n.agent ? ` from the agent's draft (${n.agent})` : ""} — the preparer's words; every figure is bound to its source.</>}
      </p>
    </div>
  );
}

function What({ x, issue, open, engagementId }: { x: Explainer; issue: Issue; open: (i: ActionIntent) => void; engagementId: string }) {
  const n = x.narrative;
  const act = (k: "draft" | "accept" | "stands", kind: "primary" | "secondary" = "secondary") => {
    const a = n.acts?.[k];
    if (!a?.command) return null;
    return <Button key={k} size="sm" kind={kind} data-command={a.command}
      onClick={() => open({ command: a.command!, engagementId, fixed: a.payload, title: `${a.label}: ${issue.title}` })}>{a.label}</Button>;
  };
  return (
    <Section id="what" title="What happened" action={<div className="flex flex-wrap gap-1.5">{act("accept", "primary")}{act("stands")}{act("draft")}</div>}>
      <div className="space-y-2">
        <p className="text-sm"><span className="text-xs font-medium uppercase tracking-wide text-muted">The record says </span>{x.what.statement}</p>
        {n.stale && (
          <div role="alert" className="rounded-md border border-stale-line bg-stale-bg px-3 py-2 text-sm">
            <p className="font-medium">What this explanation rests on changed since it was accepted — review it.</p>
            <ul className="mt-1 list-disc pl-5 text-xs">{n.flags.filter((f) => f.status === "open").map((f) => <li key={f.id}>{f.why}</li>)}</ul>
          </div>
        )}
        {n.accepted && <NarrativeBlock n={n.accepted} tone="accepted" />}
        {n.proposed && <NarrativeBlock n={n.proposed} tone="proposed" />}
        {!n.accepted && !n.proposed && <p className="text-sm text-muted">No explanation in words yet. Ask the agent to draft one: its figures are bound to the facts below, and you accept it.</p>}
      </div>
    </Section>
  );
}

// ─────────────────────────────────────────────────────────────── WHY

export function RiskList({ risks, label }: { risks: IssueRisk[]; label: string }) {
  return (
    <ul className="space-y-1.5 text-sm" aria-label={label}>
      {risks.map((r) => (
        <li key={r.id}><span className="font-medium">{r.area.replace(/_/g, " ")} · {r.assertion}</span> — IR {r.inherent}, CR {r.control}, RMM {r.rmm}{r.significant ? " · significant" : ""}{r.fraud ? " · fraud" : ""}
          <span className="block text-xs text-muted">{r.rationale} — {r.by}, {fmtDate(r.at)} (risk record {r.id})</span></li>
      ))}
    </ul>
  );
}

/** The amounts the engine measured from the issue's own facts (F42) — its ledger rows, the year's movement of its balance-sheet accounts,
 * what the record carries — largest first; a person picks the one that counts (issue.measure, through the inbox). The UI picks nothing. */
function Measures({ x, issue, open, engagementId }: { x: Explainer; issue: Issue; open: (i: ActionIntent) => void; engagementId: string }) {
  const w = x.why;
  const ms = w.measures ?? [];
  const picked = w.measure?.accepted ?? null;
  const proposed = w.measure?.proposed ?? null;
  const accept = w.measure_acts?.accept;
  if (!ms.length && !proposed && !picked) return null;
  const send = (command: string, payload: Record<string, unknown>, title: string) => open({ command, engagementId, fixed: payload, title: `${title}: ${issue.title}` });
  return (
    <div className="space-y-2">
      {picked?.changed && <p className="text-xs text-stale">The measure carried {picked.picked_text} when {picked.accepted_by} picked it and carries {picked.text} now.</p>}
      {picked?.error && <p className="text-xs text-blocked">The measure {picked.accepted_by} picked no longer resolves: {picked.error}</p>}
      {proposed && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-agent-line bg-agent-bg px-3 py-2 text-sm" aria-label="Proposed measure">
          <span>The agent (<span className="font-mono">{proposed.agent ?? proposed.by}</span>) proposes <span className="font-mono font-semibold">{proposed.text ?? proposed.picked_text}</span> as the amount at issue — {proposed.basis} <Cite c={proposed.cite} /></span>
          {accept?.command && <Button size="sm" kind="primary" data-command={accept.command} onClick={() => send(accept.command!, accept.payload, accept.label ?? "Accept the proposed measure")}>{accept.label}</Button>}
        </div>
      )}
      {ms.length > 0 && (
        <details open={!w.amount} className="text-sm">
          <summary className="cursor-pointer text-xs font-medium text-muted">Amounts measured from the issue's own facts · {ms.length}{w.measure_note ? ` — ${w.measure_note}` : ""}</summary>
          <div className="mt-1 overflow-x-auto">
            <table className="w-full min-w-[560px] text-xs" aria-label="Measured amounts">
              <thead className="text-left text-muted"><tr><th className="py-1 pr-2 text-right font-medium">Amount</th><th className="py-1 pr-2 text-right font-medium">Of planning</th>
                <th className="py-1 pr-2 font-medium">What it is</th><th className="py-1 font-medium"><span className="sr-only">Pick</span></th></tr></thead>
              <tbody className="divide-y divide-line">
                {ms.map((m) => {
                  const current = picked?.cite === m.cite;
                  return (
                    <tr key={m.cite} className={`align-top ${current ? "bg-brand/10" : ""}`}>
                      <td className="py-1 pr-2 text-right font-mono">{m.text}</td><td className="py-1 pr-2 text-right font-mono">{m.pct_planning ?? "—"}</td>
                      <td className="py-1 pr-2">{m.basis} <Cite c={m.cite} /></td>
                      <td className="py-1 text-right">{current ? <span className="text-muted">the amount at issue</span> : m.act?.command && (
                        <Button size="sm" kind="secondary" data-command={m.act.command} aria-label={`Use ${m.text} (${m.cite}) as the amount at issue`}
                          onClick={() => send(m.act!.command!, m.act!.payload, `Use ${m.text} as the amount at issue`)}>Use</Button>)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </details>
      )}
    </div>
  );
}

function Why({ x, issue, open, engagementId }: { x: Explainer; issue: Issue; open: (i: ActionIntent) => void; engagementId: string }) {
  const w = x.why;
  const r = w.risks;
  const m = w.materiality;
  return (
    <Section id="why" title="Why it matters">
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="space-y-2">
          {w.assertions.length > 0 && <p className="text-sm">The step tests {w.assertions.map((a, i) => <Fragment key={a.code}>{i ? ", " : ""}<span className="font-medium">{a.code}</span> ({a.words})</Fragment>)}.</p>}
          {r && r.risks.length > 0 && <RiskList risks={r.risks} label="Risks on these assertions" />}
          {r && r.risks.length === 0 && (r.area_risks ?? []).length > 0 && (
            <div><p className="text-xs font-medium text-muted">Area risks — the step's assertions are not among the rated ones</p><RiskList risks={r.area_risks!} label="Area risks" /></div>
          )}
          {r && r.risks.length === 0 && !(r.area_risks ?? []).length && <p className="text-sm text-muted">No assessed risk: {r.why}.</p>}
        </div>
        <div className="space-y-2">
          {w.amount ? <p className="text-sm">Amount at issue <span className="font-mono font-semibold">{w.amount.text}</span> <span className="text-xs text-muted">— {w.amount.source ? `from ${w.amount.source}: ` : ""}{w.amount.basis}</span> <Cite c={w.amount.cite} /></p>
            : <p className="text-sm text-muted">No amount is measured for this issue yet{(w.measures ?? []).length
              ? " — no record states a difference or misstatement for it; pick the amount that counts among those measured from its readings, ledger rows and trial balance."
              : " (no record states a difference or misstatement for it)."}</p>}
          <Measures x={x} issue={issue} open={open} engagementId={engagementId} />
          {m ? (
            <table className="w-full text-sm" aria-label="Against materiality">
              <thead className="text-left text-xs text-muted"><tr><th className="py-1 font-medium">Materiality (record {m.materiality_id}, {m.set_by})</th><th className="py-1 text-right font-medium">Amount</th><th className="py-1 text-right font-medium">Issue as %</th></tr></thead>
              <tbody className="divide-y divide-line">
                {(["planning", "performance", "trivial"] as const).map((k) => (
                  <tr key={k}><td className="py-1">{k === "trivial" ? "Clearly trivial" : k === "planning" ? "Planning" : "Performance"} <Cite c={m.cites[k]} /></td>
                    <td className="py-1 text-right font-mono">{{ planning: m.planning_text, performance: m.performance_text, trivial: m.trivial_text }[k]}</td><td className="py-1 text-right font-mono">{m.ratios?.[k] ?? "—"}</td></tr>
                ))}
              </tbody>
            </table>
          ) : <p className="text-sm text-muted">{w.materiality_why}</p>}
          {m?.level && <p className="text-sm">The amount is <span className="font-medium">{m.level}</span>.</p>}
        </div>
      </div>
      <div className="mt-3 grid gap-4 lg:grid-cols-2">
        <Indicators title="Points to an error" items={w.indicators.error} empty="No computed difference or factual adjustment points to an error." />
        <Indicators title="Possible fraud indicators to weigh" items={w.indicators.fraud} empty="None found in the entries and risks of this issue." />
      </div>
      <p className="mt-2 text-xs text-muted">{w.indicators.note} {w.indicators.standard}</p>
    </Section>
  );
}

function Indicators({ title, items, empty }: { title: string; items: Array<{ text: string; cite?: string }>; empty: string }) {
  return (
    <div><h4 className="text-xs font-semibold text-muted">{title}</h4>
      {items.length ? <ul className="mt-1 list-disc space-y-0.5 pl-5 text-sm" aria-label={title}>{items.map((i, n) => <li key={n}>{i.text} <Cite c={i.cite} /></li>)}</ul>
        : <p className="mt-1 text-sm text-muted">{empty}</p>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────── WHERE

function Where({ x }: { x: Explainer }) {
  const ws = useWs();
  const w = x.where;
  const gl = w.gl;
  return (
    <Section id="where" title="Where">
      <div className="space-y-4">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm" aria-label="Accounts involved">
            <thead className="text-left text-xs text-muted"><tr><th className="py-1 pr-2 font-medium">Account</th><th className="py-1 pr-2 text-right font-medium">Current year</th>
              <th className="py-1 pr-2 text-right font-medium">Prior year</th><th className="py-1 pr-2 font-medium">Statement line · area</th><th className="py-1 font-medium">Why it is here</th></tr></thead>
            <tbody className="divide-y divide-line">
              {w.accounts.map((a) => (
                <tr key={a.code} id={anchor(`acct-${a.code}`)} tabIndex={-1} className="align-top">
                  <td className="py-1 pr-2"><span className="font-mono">{a.code}</span> {a.name} <Cite c={a.cite} /></td>
                  <td className="py-1 pr-2 text-right font-mono">{a.cy_text ?? "—"}</td><td className="py-1 pr-2 text-right font-mono">{a.py_text ?? "—"}</td>
                  <td className="py-1 pr-2 text-xs"><a className="text-brand hover:underline" href={`#${anchor(`fs-${a.fs_line ?? ""}`)}`}>{a.fs_line ?? "—"}</a>{a.area ? ` · ${a.area.replace(/_/g, " ")}` : ""}</td>
                  <td className="py-1 text-xs text-muted">{a.why.join("; ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {w.accounts.length === 0 && <p className="text-sm text-muted">The issue names no account.</p>}
        </div>
        {w.area_accounts.length > 0 && (
          <details className="text-sm"><summary className="cursor-pointer text-xs text-muted">Other accounts in the area · {w.area_accounts.length}</summary>
            <ul className="mt-1 space-y-0.5 text-xs">{w.area_accounts.map((a) => <li key={a.code}><span className="font-mono">{a.code}</span> {a.name} <span className="font-mono">{a.cy_text ?? "—"}</span> · {a.fs_line}</li>)}</ul>
          </details>
        )}
        {w.fs_lines.length > 0 && (
          <ul className="space-y-1 text-sm" aria-label="Statement lines">
            {w.fs_lines.map((f) => (
              <li key={f.fs_line} id={anchor(`fs-${f.fs_line}`)} tabIndex={-1}><span className="font-medium">{f.fs_line}</span>: accounts {f.accounts.join(", ")} total <span className="font-mono">{f.cy_text}</span>
                {f.statement && <span className="block text-xs text-muted">Client's {f.statement.statement} “{f.statement.caption}” shows {f.statement.amount_text}; the trial balance accounts it presents ({(f.statement.accounts ?? []).join(", ")}) total {f.statement.tb_total_text}{f.statement.difference_text ? ` — differs by ${f.statement.difference_text}` : " — agrees"} <Cite c={f.statement.source} /></span>}</li>
            ))}
          </ul>
        )}
        <div><h4 className="text-xs font-semibold text-muted">Workpapers</h4>
          <ul className="mt-1 space-y-0.5 text-sm" aria-label="Workpapers involved">{w.workpapers.map((p) => (
            <li key={p.template}>{p.ref && p.id ? <Link className="text-brand hover:underline" to={`/e/${ws.id}/wp/${encodeURIComponent(p.ref)}`}>{p.ref} {p.name}</Link> : <span>{p.ref ?? p.template} {p.name}</span>}
              <span className="text-xs text-muted"> — {p.why} ({p.status_label})</span></li>))}</ul>
        </div>
        <div>
          <h4 className="text-xs font-semibold text-muted">General ledger</h4>
          {!gl.imported ? <p className="text-sm text-muted">{gl.why}</p> : (
            <div className="space-y-2">
              <p className="text-xs text-muted">From {gl.source} (imported by {gl.imported_by}{gl.imported_at ? `, ${fmtDate(gl.imported_at)}` : ""}): {gl.rows.length} of {gl.total_rows} entries on these accounts listed — the ones at the amount at issue first. {gl.entered_by_note}.</p>
              <ul className="space-y-0.5 text-xs" aria-label="Ledger totals">{gl.accounts.map((a) => (
                <li key={a.code} id={anchor(`glacct-${a.code}`)} tabIndex={-1} className={a.ties ? "" : "text-blocked"}><span className="font-mono">{a.code}</span>: {a.entries} entries, opening {a.opening_text} + activity {a.activity_text} {a.ties ? `= trial balance ${a.tb_text}` : `≠ trial balance ${a.tb_text} (difference ${a.difference_text})`}</li>))}</ul>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[720px] text-xs" aria-label="Ledger entries">
                  <thead className="text-left text-muted"><tr><th className="py-1 pr-2 font-medium">Date</th><th className="py-1 pr-2 font-medium">Account</th><th className="py-1 pr-2 text-right font-medium">Debit</th>
                    <th className="py-1 pr-2 text-right font-medium">Credit</th><th className="py-1 pr-2 font-medium">Reference · memo · name</th><th className="py-1 pr-2 font-medium">Against</th><th className="py-1 font-medium">Why listed</th></tr></thead>
                  <tbody className="divide-y divide-line">
                    {gl.rows.map((r) => (
                      <tr key={r.cite} id={anchor(`gl-${r.cite}`)} tabIndex={-1} className={`align-top ${r.why.some((y) => y.startsWith("the amount")) ? "bg-waiting-bg" : ""}`}>
                        <td className="whitespace-nowrap py-1 pr-2">{r.date}{r.after_year_end && <span className="ml-1 text-blocked">after year end</span>}</td>
                        <td className="py-1 pr-2 font-mono">{r.account}</td>
                        <td className="py-1 pr-2 text-right font-mono">{r.side === "debit" ? r.amount_text : ""}</td>
                        <td className="py-1 pr-2 text-right font-mono">{r.side === "credit" ? r.amount_text.replace(/[()]/g, "") : ""}</td>
                        <td className="py-1 pr-2">{[r.reference, r.memo, r.name, r.type].filter(Boolean).join(" · ")} <Cite c={r.cite} /></td>
                        <td className="py-1 pr-2 font-mono">{r.offsets.join(", ") || "—"}</td>
                        <td className="py-1 text-muted">{r.why.join("; ")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
        <div><h4 className="text-xs font-semibold text-muted">Documents</h4>
          {w.documents.length === 0 ? <p className="text-sm text-muted">No document in the vault carries this issue yet.</p> : (
            <ul className="mt-1 space-y-2 text-sm" aria-label="Documents involved">
              {w.documents.map((d) => (
                <li key={d.sha256} className="rounded-md border border-line px-3 py-2">
                  <p><Link className="font-medium text-brand hover:underline" to={`/e/${ws.id}/details/evidence?doc=${d.sha256}`}>{d.name}</Link>
                    <span className="text-xs text-muted"> — {d.folder}, received {fmtDate(d.received_at)} by {d.received_by}; {d.via}{d.replaced ? "; replaced by the client" : ""}</span></p>
                  {d.readings.length > 0 && <ul className="mt-1 space-y-0.5 text-xs">{d.readings.map((r) => (
                    <li key={r.id}>{r.label}{r.value !== null ? <>: <span className="font-mono">{r.value}</span></> : ""} — page {r.page ?? "—"}, {r.locator} · {r.confirmation} <Cite c={r.cite} /></li>))}</ul>}
                  {(d.imports ?? []).length > 0 && <ul className="mt-1 space-y-0.5 text-xs" aria-label={`How ${d.name} was imported`}>{d.imports!.map((m, n) => (
                    <li key={n} className={m.current ? "" : "text-muted"}>{m.text} — the import is its reading{m.at ? `, ${fmtDate(m.at)}` : ""}</li>))}</ul>}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Section>
  );
}

// ─────────────────────────────────────────────────────────────── WHEN

function When({ x }: { x: Explainer }) {
  const ye = x.when.findIndex((e) => e.kind === "period" && e.text.startsWith("Year end"));
  return (
    <Section id="when" title="When">
      <ol className="space-y-1.5 border-l border-line pl-4 text-sm" aria-label="Timeline">
        {x.when.map((e, n) => (
          <li key={n} className="relative">
            <span aria-hidden="true" className={`absolute -left-[1.3rem] top-1.5 h-2 w-2 rounded-full ${e.kind === "period" ? "bg-ink" : e.after_year_end ? "bg-waiting" : "bg-line"}`} />
            <span className="mr-2 font-mono text-xs text-muted">{e.date}</span>
            <span className={n === ye ? "font-semibold" : ""}>{e.link ? <To link={e.link}>{e.text}</To> : e.text}</span>
            {e.after_year_end && e.kind !== "audit" && <span className="ml-1 text-[11px] text-waiting">after year end</span>}
            <span className="ml-1 text-[11px] text-muted">{e.kind}</span> <Cite c={e.cite} />
          </li>
        ))}
      </ol>
    </Section>
  );
}

// ─────────────────────────────────────────────────────────────── HOW — the flow

const COL_W = 200, NODE_W = 176, NODE_H = 66, GAP = 14, PAD = 12, TOP = 48, CHANNEL = 34;   // an edge that skips a column runs in the channel above the nodes

export function FlowDiagram({ how }: { how: Explainer["how"] }) {
  const { go } = useExplainLink();
  const layers = how.layers.map((l) => l.id);
  const cols = new Map(layers.map((l) => [l, how.nodes.filter((n) => n.layer === l)] as const));
  const pos = new Map<string, { x: number; y: number }>();
  layers.forEach((l, i) => cols.get(l)!.forEach((n, j) => pos.set(n.id, { x: PAD + i * COL_W, y: TOP + j * (NODE_H + GAP) })));
  const rows = Math.max(1, ...[...cols.values()].map((v) => v.length));
  const W = PAD * 2 + layers.length * COL_W - (COL_W - NODE_W), H = TOP + rows * (NODE_H + GAP) + PAD;
  const cut = (s: string | null, k: number) => (!s ? "" : s.length > k ? `${s.slice(0, k - 1)}…` : s);
  const tone = (n: ExNode) => (n.status === "break" ? "fill-blocked-bg stroke-blocked" : n.status === "missing" ? "fill-sunken stroke-muted" : n.status === "info" ? "fill-paper stroke-line" : "fill-raised stroke-line");
  const label = (n: ExNode) => `${n.label}${n.amount_text ? `, ${n.amount_text}` : ""}${n.detail ? `; ${n.detail}` : ""}${n.status === "break" ? " — where it goes wrong" : n.status === "missing" ? " — missing" : ""}`;
  return (
    <div className="overflow-x-auto rounded-md border border-line bg-paper">
      <svg viewBox={`0 0 ${W} ${H}`} style={{ minWidth: Math.min(W, 640), width: "100%" }} role="img" aria-label="Data flow from the source document to the statements, with the step where it goes wrong">
        <defs><marker id="ex-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse"><path d="M 0 0 L 10 5 L 0 10 z" className="fill-muted" /></marker></defs>
        {how.layers.map((l, i) => <text key={l.id} x={PAD + i * COL_W} y={18} className="fill-muted text-[11px] font-semibold uppercase">{l.label}</text>)}
        {how.edges.map((e, i) => {
          const a = pos.get(e.from), b = pos.get(e.to);
          if (!a || !b) return null;
          const x1 = a.x + NODE_W, y1 = a.y + NODE_H / 2, x2 = b.x, y2 = b.y + NODE_H / 2;
          const back = x2 <= x1, skip = x2 - x1 > COL_W;
          const d = back ? `M ${a.x + NODE_W / 2} ${a.y + NODE_H} C ${a.x + NODE_W / 2} ${a.y + NODE_H + 30}, ${b.x + NODE_W / 2} ${b.y + NODE_H + 30}, ${b.x + NODE_W / 2} ${b.y + NODE_H}`
            : skip ? `M ${x1} ${y1} C ${x1 + 12} ${y1}, ${x1 + 12} ${CHANNEL}, ${x1 + 24} ${CHANNEL} L ${x2 - 24} ${CHANNEL} C ${x2 - 12} ${CHANNEL}, ${x2 - 12} ${y2}, ${x2} ${y2}`
            : `M ${x1} ${y1} C ${x1 + 14} ${y1}, ${x2 - 14} ${y2}, ${x2} ${y2}`;
          return <path key={i} d={d} fill="none" markerEnd="url(#ex-arrow)" strokeWidth={e.status === "break" ? 2.5 : 1.25}
            className={e.status === "break" ? "stroke-blocked" : "stroke-muted"} strokeDasharray={e.status === "break" ? "5 3" : undefined}><title>{e.label}</title></path>;
        })}
        {how.nodes.map((n) => {
          const p = pos.get(n.id)!;
          return (
            <g key={n.id} transform={`translate(${p.x} ${p.y})`} role={n.link ? "link" : undefined} tabIndex={n.link ? 0 : undefined} aria-label={label(n)}
              data-node={n.id} data-status={n.status} className={n.link ? "cursor-pointer focus:outline-none" : undefined}
              onClick={() => go(n.link)} onKeyDown={(ev) => { if (ev.key === "Enter" || ev.key === " ") { ev.preventDefault(); go(n.link); } }}>
              <title>{label(n)}</title>
              <rect width={NODE_W} height={NODE_H} rx={8} strokeWidth={n.status === "break" ? 2.5 : 1} className={tone(n)} />
              <text x={10} y={20} className={`text-[12px] font-semibold ${n.status === "break" ? "fill-blocked" : "fill-ink"}`}>{cut(n.label, 26)}</text>
              <text x={10} y={37} className="fill-muted text-[11px]">{cut(n.detail, 30)}</text>
              {n.amount_text && <text x={10} y={55} className="fill-ink font-mono text-[11px]">{n.amount_text}</text>}
              {n.status === "break" && <text x={NODE_W - 10} y={55} textAnchor="end" className="fill-blocked text-[10px] font-semibold uppercase">breaks here</text>}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function How({ x }: { x: Explainer }) {
  const h = x.how;
  const brk = h.nodes.find((n) => n.id === h.break.node);
  return (
    <Section id="how" title="How — the data flow">
      {h.nodes.length === 0 ? <p className="text-sm text-muted">This kind of issue has no flow of entries to draw.</p> : (
        <div className="space-y-2">
          <FlowDiagram how={h} />
          <p className={`rounded-md border px-3 py-2 text-sm ${h.break.node ? "border-blocked-line bg-blocked-bg" : "border-line"}`} role="note" aria-label="Where it goes wrong">
            <span className="font-medium">{brk ? `Where it goes wrong: ${brk.label}. ` : "Where it goes wrong: "}</span>{h.break.text}
{" "}<span className="text-xs text-muted">(basis: {h.break.basis})</span>
          </p>
          {(h.break.candidates ?? []).length > 0 && (
            <details className="text-xs">
              <summary className="cursor-pointer text-muted">How it was ranked · {h.break.candidates!.length} place{h.break.candidates!.length === 1 ? "" : "s"} with an amount</summary>
              {h.break.ranking && <p className="mt-1 text-muted">{h.break.ranking}.</p>}
              <ol className="mt-1 list-decimal space-y-0.5 pl-5" aria-label="Ranked break points">
                {h.break.candidates!.map((c) => (
                  <li key={c.node} className={c.node === h.break.node ? "font-semibold" : ""}>
                    <span className="font-mono">{c.amount_text}</span>{c.pct_planning ? ` (${c.pct_planning} of planning)` : ""} — {h.nodes.find((n) => n.id === c.node)?.label ?? c.node} · {c.basis}: {c.text}
                  </li>
                ))}
              </ol>
            </details>
          )}
          <ol className="grid gap-1 text-xs sm:grid-cols-2" aria-label="Flow steps">
            {h.layers.map((l) => h.nodes.filter((n) => n.layer === l.id).map((n) => (
              <li key={n.id} className={n.status === "break" ? "font-semibold text-blocked" : n.status === "missing" ? "text-muted" : ""}>
                <span className="text-muted">{l.label}:</span> <To link={n.link}>{n.label}</To>{n.amount_text ? ` ${n.amount_text}` : ""}{n.status === "break" ? " — breaks here" : n.status === "missing" ? " — missing" : ""}
              </li>
            )))}
          </ol>
        </div>
      )}
    </Section>
  );
}

// ─────────────────────────────────────────────────────────────── WHAT ELSE, WHAT CLOSES IT

function Impact({ x }: { x: Explainer }) {
  const ws = useWs();
  const im = x.impact;
  return (
    <Section id="impact" title="What else it affects">
      <div className="grid gap-4 lg:grid-cols-2">
        <div><h4 className="text-xs font-semibold text-muted">Downstream workpapers (dependency graph) · {im.downstream_total}</h4>
          {im.downstream.length === 0 ? <p className="mt-1 text-sm text-muted">Nothing in the dependency graph rests on it.</p> : (
            <ul className="mt-1 space-y-0.5 text-sm" aria-label="Downstream workpapers">{im.downstream.map((d) => (
              <li key={d.template} className={d.depth > 1 ? "pl-3 text-muted" : ""}>{d.ref && d.opened ? <Link className="text-brand hover:underline" to={`/e/${ws.id}/wp/${encodeURIComponent(d.ref)}`}>{d.label} {d.name}</Link> : <span>{d.label} {d.name}</span>}
                <span className="text-xs text-muted"> — {d.relation}{d.depth > 1 ? ` via ${d.via}` : ""} ({d.status_label})</span></li>))}</ul>)}
        </div>
        <div className="space-y-3">
          <div><h4 className="text-xs font-semibold text-muted">Related issues</h4>
            {im.related.length === 0 ? <p className="mt-1 text-sm text-muted">No other issue shares its ledger entries, amounts, documents, accounts or area.</p> : (
              <ul className="mt-1 space-y-1 text-sm" aria-label="Related issues">{im.related.map((r) => (
                <li key={r.key}><Link className="text-brand hover:underline" to={issueHref(ws.id, r.key)}>{r.title}</Link> <IssueStatusChip issue={r} />{r.strength === "strong" && <span className="text-xs text-muted"> (bound up with this one)</span>}
                  {(r.reasons ?? []).length > 0
                    ? <ul className="list-disc pl-5 text-xs text-muted" aria-label={`Why ${r.key} is related`}>{r.reasons!.map((y, n) => <li key={n}>{y}</li>)}</ul>
                    : <span className="text-xs text-muted"> — {r.why}</span>}</li>))}</ul>)}
          </div>
          <div><h4 className="text-xs font-semibold text-muted">Disclosures</h4>
            {im.disclosures.length === 0 ? <p className="mt-1 text-sm text-muted">{im.disclosure_note}</p> : (
              <ul className="mt-1 space-y-0.5 text-sm">{im.disclosures.map((d) => <li key={d.id}>{d.topic} ({d.area.replace(/_/g, " ")}{d.account ? `, ${d.account}` : ""}): {d.nature}{d.amount_text ? ` — ${d.amount_text}` : ""}</li>)}</ul>)}
          </div>
        </div>
      </div>
      {im.adjustments.length > 0 && (
        <div className="mt-3 space-y-2"><h4 className="text-xs font-semibold text-muted">Proposed adjustments and their effect on the statements</h4>
          {im.adjustments.map((a) => (
            <div key={a.number} className="rounded-md border border-line px-3 py-2 text-sm">
              <p><Link className="font-medium text-brand hover:underline" to={issueHref(ws.id, `adj:${a.number}`)}>{a.number}</Link> ({a.disposition}): {a.description}</p>
              <p className="text-xs">Change in net assets <span className="font-mono">{a.change_in_net_assets_text}</span> · total assets <span className="font-mono">{a.total_assets_text}</span> · total liabilities <span className="font-mono">{a.total_liabilities_text}</span></p>
              <p className="text-xs text-muted">{a.by_fs_line.map((f) => `${f.fs_line} ${f.effect_text}`).join(" · ")}</p>
            </div>
          ))}
        </div>
      )}
      {im.report.length > 0 && (
        <div className="mt-3"><h4 className="text-xs font-semibold text-muted">If it stays uncorrected — considerations for the report, not conclusions</h4>
          <ul className="mt-1 list-disc space-y-0.5 pl-5 text-sm" aria-label="Report considerations">{im.report.map((r, n) => <li key={n}>{r.text} <span className="text-xs text-muted">{r.standard}</span></li>)}</ul>
        </div>
      )}
    </Section>
  );
}

function Closes({ x }: { x: Explainer }) {
  const ws = useWs();
  const c = x.closes;
  return (
    <Section id="closes" title="What closes it">
      <div className="grid gap-4 lg:grid-cols-3">
        <div><h4 className="text-xs font-semibold text-muted">Client questions and requests</h4>
          {c.threads.length + c.requests.length === 0 ? <p className="mt-1 text-sm text-muted">None linked yet.</p> : (
            <ul className="mt-1 space-y-0.5 text-sm">
              {c.requests.map((r) => <li key={`r${r.id}`}><Link className="text-brand hover:underline" to={`/e/${ws.id}/documents#pbc-${r.id}`}>PBC-{r.id} {r.text}</Link> <span className="text-xs text-muted">({r.status})</span></li>)}
              {c.threads.map((t) => <li key={`t${t.id}`}><Link className="text-brand hover:underline" to={`/e/${ws.id}/documents`}>{t.subject}</Link> <span className="text-xs text-muted">({t.kind}, {t.status})</span></li>)}
            </ul>)}
        </div>
        <div><h4 className="text-xs font-semibold text-muted">Evidence that would resolve it</h4>
          <ul className="mt-1 list-disc space-y-0.5 pl-5 text-sm" aria-label="Evidence that would resolve it">{c.evidence_needed.map((e, n) => <li key={n}><span className="font-medium">{e.for}:</span> {e.evidence}</li>)}</ul>
        </div>
        <div><h4 className="text-xs font-semibold text-muted">Ways to close it</h4>
          <ul className="mt-1 space-y-0.5 text-sm" aria-label="Ways to close it">{c.options.map((o) => <li key={o.outcome}><StatusPill kind="neutral" label={o.label} /> <span className="text-xs text-muted">needs {o.needs}</span></li>)}</ul>
        </div>
      </div>
      <p className="mt-2 text-xs text-muted">{c.note}</p>
    </Section>
  );
}

/** The narrative's plain text (tests, copy). */
export const narrativeText = (n: ExNarrative) => `${segmentsText(n.what)} ${segmentsText(n.why)}`;
